import React, { useState, useEffect, useMemo } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc,
  query,
  deleteDoc
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus, Users, Search, Trash2, 
  RefreshCw, X, MapPin, Plus, Smartphone, Calendar, Home, DollarSign, 
  Store, Filter, Banknote, UserMinus, History, Phone, 
  UserIcon, ShieldAlert, Building2
} from "lucide-react";
import Sidebar from "../components/Sidebar";

// =========================================================================
// 1. ADD EMPLOYEE MODAL (Defined Outside to Prevent Re-render Focus Loss)
// =========================================================================
const AddEmployeeModal = ({ isOpen, onClose, onSave, isProcessing, stores }) => {
  const [formData, setFormData] = useState({
    name: "", phone: "", role: "Staff", salary: "", dateOfJoin: new Date().toISOString().split("T")[0], address: "", storeId: ""
  });

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: "", phone: "", role: "Staff", salary: "", dateOfJoin: new Date().toISOString().split("T")[0], address: "", storeId: "" });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const roles = ["Owner", "Manager", "Optometrist", "Cashier", "Staff"];

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="w-full max-w-xl bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
          <h3 className="text-lg font-black uppercase text-slate-800 tracking-wider flex items-center gap-2">
            <UserPlus size={20} className="text-blue-600" /> Onboard Global Associate
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-rose-500 bg-slate-50 p-2 rounded-full transition-colors"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs font-medium">
          <div className="space-y-1.5">
            <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">* Assign to Branch</label>
            <select name="storeId" value={formData.storeId} onChange={handleChange} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 font-bold rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-colors cursor-pointer" required>
              <option value="">-- Select Store Branch --</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.storeId || s.storeId || s.id}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">* Full Name</label>
              <input type="text" name="name" placeholder="Ex: Anjali Verma" value={formData.name} onChange={handleChange} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-colors" required />
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">* Contact Number</label>
              <div className="relative">
                <Smartphone size={16} className="absolute left-4 top-4 text-slate-400" />
                <input type="tel" name="phone" maxLength="10" placeholder="Ex: 9876xxxxxx" value={formData.phone} onChange={handleChange} className="w-full pl-11 pr-3 py-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-blue-500 focus:bg-white font-mono transition-colors" required />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Assigned Role</label>
              <select name="role" value={formData.role} onChange={handleChange} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-colors cursor-pointer">
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Base Salary (₹)</label>
              <div className="relative">
                <DollarSign size={16} className="absolute left-4 top-4 text-slate-400" />
                <input type="number" name="salary" placeholder="Ex: 25000" value={formData.salary} onChange={handleChange} className="w-full pl-11 pr-3 py-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-blue-500 focus:bg-white font-mono font-bold transition-colors" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Date of Joining</label>
              <div className="relative">
                <Calendar size={16} className="absolute left-4 top-4 text-slate-400" />
                <input type="date" name="dateOfJoin" value={formData.dateOfJoin} onChange={handleChange} className="w-full pl-11 pr-3 py-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-blue-500 focus:bg-white font-mono transition-colors cursor-pointer" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Residential Address</label>
              <div className="relative">
                <Home size={16} className="absolute left-4 top-4 text-slate-400" />
                <input type="text" name="address" placeholder="Ex: Flat 402, Bihar" value={formData.address} onChange={handleChange} className="w-full pl-11 pr-3 py-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-colors" />
              </div>
            </div>
          </div>

          <div className="pt-5">
            <button type="submit" disabled={isProcessing} className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-wider rounded-xl transition-all shadow-md shadow-blue-200 flex items-center justify-center gap-2">
              {isProcessing ? <RefreshCw size={18} className="animate-spin" /> : <Plus size={18} />} Save Employee Profile
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// =========================================================================
// 2. MAIN SUPER ADMIN COMPONENT
// =========================================================================
export default function SuperAdminEmployees() {
  const [stores, setStores] = useState([]);
  const [employeesByStore, setEmployeesByStore] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Active"); 
  const [branchFilter, setBranchFilter] = useState("All");

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [auditTab, setAuditTab] = useState("profile"); 

  // Payroll States
  const [activePayrollEmp, setActivePayrollEmp] = useState(null);
  const [payrollForm, setPayrollForm] = useState({ amount: "", date: new Date().toISOString().split("T")[0], mode: "UPI / Digital", type: "Standard Salary", notes: "" });

  const roles = ["All", "Owner", "Manager", "Optometrist", "Cashier", "Staff"];

  // --- FETCH ALL STORES ---
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "stores"), (snapshot) => {
      setStores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  // --- FETCH EMPLOYEES ACROSS ALL STORES ---
  useEffect(() => {
    if (stores.length === 0) return;
    
    // Attach a listener to each store's employee collection
    const unsubscribes = stores.map(store => {
      const empRef = collection(db, "stores", store.id, "employees");
      return onSnapshot(empRef, (snapshot) => {
        const storeEmployees = snapshot.docs.map(doc => ({
          id: doc.id,
          storeId: store.id,
          storeName: store.storeId || store.storeId || store.id,
          ...doc.data()
        }));
        
        // Update the dictionary of employees by store
        setEmployeesByStore(prev => ({
          ...prev,
          [store.id]: storeEmployees
        }));
      });
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [stores]);

  // --- AGGREGATE & FILTER ---
  const allEmployees = useMemo(() => Object.values(employeesByStore).flat(), [employeesByStore]);

  const filteredEmployees = useMemo(() => {
    return allEmployees.filter((e) => {
      const matchSearch = e?.name?.toLowerCase().includes(search.toLowerCase()) || e?.phone?.includes(search);
      const matchRole = roleFilter === "All" || e?.role === roleFilter;
      const matchStatus = statusFilter === "All" || (e?.status || "Active") === statusFilter;
      const matchBranch = branchFilter === "All" || e?.storeId === branchFilter;
      return matchSearch && matchRole && matchStatus && matchBranch;
    });
  }, [allEmployees, search, roleFilter, statusFilter, branchFilter]);

  // --- MASTER METRICS ---
  const globalMonthlySalary = useMemo(() => {
    return allEmployees.filter(e => (e.status || "Active") === "Active").reduce((sum, e) => sum + (Number(e?.salary) || 0), 0);
  }, [allEmployees]);

  const branchCount = stores.length;

  // --- HANDLERS ---
  const handleSaveEmployee = async (formData) => {
    if (!formData.storeId) return alert("Please select a branch to assign the employee to.");
    setIsProcessing(true);
    try {
      await addDoc(collection(db, "stores", formData.storeId, "employees"), {
        name: formData.name.trim(),
        role: formData.role,
        status: "Active",
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        salary: Number(formData.salary) || 0,
        dateOfJoin: formData.dateOfJoin,
        salaryHistory: [], 
        createdAt: new Date().toISOString()
      });
      setIsAddModalOpen(false);
    } catch (err) { alert("Error adding employee."); } 
    finally { setIsProcessing(false); }
  };

  const handleDeactivateStaff = async (employee) => {
    const isDeactivating = employee.status !== "Past Employee";
    const actionText = isDeactivating ? `Deactivate ${employee.name} across the network?` : `Reactivate ${employee.name}?`;
    if (!window.confirm(actionText)) return;
    try {
      await updateDoc(doc(db, "stores", employee.storeId, "employees", employee.id), { 
        status: isDeactivating ? "Past Employee" : "Active" 
      });
    } catch (err) { console.error("Update failed: ", err); }
  };

  const openPayrollModal = (employee) => {
    setActivePayrollEmp(employee);
    setPayrollForm({ amount: employee.salary || "", date: new Date().toISOString().split("T")[0], mode: "UPI / Digital", type: "Standard Salary", notes: "" });
    setIsPayModalOpen(true);
  };

  const handleCommitPayroll = async (e) => {
    e.preventDefault();
    if (!payrollForm.amount) return alert("Enter payment amount.");
    setIsProcessing(true);
    try {
      const docRef = doc(db, "stores", activePayrollEmp.storeId, "employees", activePayrollEmp.id);
      const paymentRecord = {
        id: Date.now().toString(),
        amount: Number(payrollForm.amount),
        date: payrollForm.date,
        mode: payrollForm.mode,
        type: payrollForm.type,
        notes: payrollForm.notes,
        timestamp: new Date().toISOString(),
        processedBy: "Super Admin"
      };

      await updateDoc(docRef, { salaryHistory: [...(activePayrollEmp.salaryHistory || []), paymentRecord] });

      await addDoc(collection(db, "stores", activePayrollEmp.storeId, "expenses"), {
        title: `HQ Payroll: ${activePayrollEmp.name} (${payrollForm.type})`,
        amount: Number(payrollForm.amount),
        category: "Salaries",
        date: payrollForm.date,
        spentBy: "Super Admin System",
        paymentMethod: payrollForm.mode,
        notes: payrollForm.notes || `Automated payroll log by HQ.`,
        branchId: activePayrollEmp.storeId,
        timestamp: new Date().toISOString()
      });

      setIsPayModalOpen(false);
      setActivePayrollEmp(null);
    } catch (error) { alert("Failed to process payroll: " + error.message); } 
    finally { setIsProcessing(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <Sidebar />

      <div className="flex-1 p-6 lg:p-10 space-y-8 overflow-x-hidden">

        {/* HEADER BLOCK */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-8 bg-white border border-slate-200 rounded-[2rem] shadow-sm relative overflow-hidden">
          <div className="relative z-10">
            <h1 className="text-3xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-3">
              <ShieldAlert size={32} className="text-blue-600" /> Master Workforce Console
            </h1>
            <div className="flex items-center gap-2 mt-2 text-sm text-slate-500 font-medium">
              <Building2 size={16} className="text-emerald-500" />
              <span>Network Wide Overview: </span>
              <span className="bg-emerald-50 text-emerald-600 font-black px-3 py-1 rounded-lg border border-emerald-100 text-xs tracking-widest uppercase">
                HQ Administrative Privileges
              </span>
            </div>
          </div>
          <button onClick={() => setIsAddModalOpen(true)} className="relative z-10 flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 font-black tracking-wider uppercase text-white shadow-md shadow-blue-200 transition-all text-xs">
            <UserPlus size={18} /> Onboard Global Staff
          </button>
        </div>

        {/* GLOBAL METRICS DASHBOARD */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-3xl bg-white border border-slate-200 flex items-center justify-between shadow-sm relative overflow-hidden">
            <div>
              <span className="text-xs text-slate-500 font-black uppercase tracking-wider">Total Franchises</span>
              <span className="block text-4xl font-black text-slate-800 font-mono mt-1">{branchCount}</span>
            </div>
            <div className="p-4 bg-slate-50 text-slate-400 border border-slate-100 rounded-2xl"><Building2 size={28} /></div>
          </div>
          
          <div className="p-6 rounded-3xl bg-white border border-slate-200 flex items-center justify-between shadow-sm relative overflow-hidden">
            <div>
              <span className="text-xs text-blue-500 font-black uppercase tracking-wider">Network Active Staff</span>
              <span className="block text-4xl font-black text-blue-600 font-mono mt-1">{allEmployees.filter(e => (e.status||"Active") === "Active").length}</span>
            </div>
            <div className="p-4 bg-blue-50 border border-blue-100 text-blue-500 rounded-2xl"><Users size={28} /></div>
          </div>

          <div className="p-6 rounded-3xl bg-emerald-600 border border-emerald-700 flex items-center justify-between shadow-md relative overflow-hidden text-white">
            <div className="absolute right-0 bottom-0 w-32 h-32 bg-emerald-500 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
            <div className="relative z-10">
              <span className="text-xs text-emerald-200 font-black uppercase tracking-wider">Global Monthly Payroll</span>
              <span className="block text-4xl font-black text-white font-mono mt-1">₹{globalMonthlySalary.toLocaleString()}</span>
            </div>
            <div className="p-4 bg-emerald-500 border border-emerald-400 text-white rounded-2xl relative z-10 shadow-sm"><DollarSign size={28} /></div>
          </div>
        </div>

        {/* FILTERS BAR */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Filter size={18} className="text-blue-500" />
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600">Global Directory Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search personnel..." className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 text-sm font-medium rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-colors" />
            </div>

            <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-blue-500 cursor-pointer">
              <option value="All">All Branches</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.storeId || s.storeId || s.id}</option>)}
            </select>

            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-blue-500 cursor-pointer">
              {roles.map(r => <option key={r} value={r}>{r === "All" ? "All Roles" : r}</option>)}
            </select>

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-blue-500 cursor-pointer">
              <option value="All">All Statuses</option>
              <option value="Active">Active Network Staff</option>
              <option value="Past Employee">Archived Employees</option>
            </select>
          </div>
        </div>

        {/* WORKFORCE GRID */}
        <div className="space-y-4">
          <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-2">
            <Store size={18} className="text-blue-500" /> Cross-Branch Roster Directory
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredEmployees.length === 0 ? (
              <div className="lg:col-span-3 py-16 bg-white border border-slate-200 rounded-3xl text-center text-slate-500 text-sm font-medium shadow-sm flex flex-col items-center gap-3">
                <Users size={48} className="text-slate-300" />
                No personnel found matching the current global filters.
              </div>
            ) : (
              filteredEmployees.map((e) => {
                const isActive = (e.status || "Active") === "Active";
                return (
                  <motion.div key={e.id} layout className={`bg-white border rounded-3xl p-6 flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden ${isActive ? 'border-slate-200' : 'border-rose-200 bg-rose-50/50'}`}>
                    
                    {/* Branch Badge */}
                    <div className="absolute top-0 right-0 bg-slate-800 text-white text-[9px] font-black uppercase tracking-widest px-4 py-1.5 rounded-bl-2xl">
                      {e.storeId}
                    </div>

                    <div className="space-y-4 pt-2">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl shadow-sm ${isActive ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-rose-100 text-rose-500 border border-rose-200'}`}>
                          {e.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-black text-lg leading-tight ${isActive ? 'text-slate-800' : 'text-slate-500 line-through'}`}>{e.name}</p>
                          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{e.role || "Staff"}</span>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-2.5 text-xs">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Mobile</span>
                          <span className="font-mono font-bold text-slate-700">{e.phone || "N/A"}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Base Salary</span>
                          <span className="font-mono font-black text-emerald-600 text-sm">₹{e.salary?.toLocaleString() || "0"}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Joined</span>
                          <span className="font-mono font-bold text-slate-700">{e.dateOfJoin || "N/A"}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-5 pt-5 border-t border-slate-100">
                      <button onClick={() => { setViewingEmployee(e); setAuditTab("profile"); }} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold uppercase tracking-wider rounded-xl text-[10px] transition-colors border border-slate-200">
                        View Profile
                      </button>
                      {isActive ? (
                        <>
                          <button onClick={() => openPayrollModal(e)} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white uppercase tracking-wider font-bold rounded-xl text-[10px] transition-colors shadow-md shadow-blue-200">
                            HQ Payroll
                          </button>
                          <button onClick={() => handleDeactivateStaff(e)} title="Mark as Past Employee" className="p-2.5 bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white border border-rose-200 rounded-xl transition-colors">
                            <UserMinus size={16} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => handleDeactivateStaff(e)} className="flex-1 py-2.5 bg-emerald-50 hover:bg-emerald-600 text-emerald-600 hover:text-white border border-emerald-200 font-bold uppercase tracking-wider rounded-xl text-[10px] transition-colors">
                          Reactivate
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* --- EXTERNAL MODALS --- */}
      <AddEmployeeModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSaveEmployee} isProcessing={isProcessing} stores={stores} />

      {/* PAYROLL MODAL (INLINE CONDITIONAL) */}
      <AnimatePresence>
        {isPayModalOpen && activePayrollEmp && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
                <div>
                  <h3 className="text-lg font-black uppercase text-slate-800 tracking-wider flex items-center gap-2">
                    <Banknote size={20} className="text-emerald-500" /> HQ Payroll Issue
                  </h3>
                  <p className="text-[10px] text-slate-500 mt-1.5 font-bold uppercase tracking-wider">
                    Target: <span className="text-blue-600">{activePayrollEmp.name}</span> | <span className="text-slate-800">{activePayrollEmp.storeId}</span>
                  </p>
                </div>
                <button onClick={() => { setIsPayModalOpen(false); setActivePayrollEmp(null); }} className="text-slate-400 hover:text-rose-500 bg-slate-50 p-2 rounded-full transition-colors"><X size={16} /></button>
              </div>

              <form onSubmit={handleCommitPayroll} className="space-y-4 text-xs font-medium">
                <div className="space-y-1.5">
                  <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Payment Amount (₹) *</label>
                  <input type="number" value={payrollForm.amount} onChange={e => setPayrollForm({...payrollForm, amount: e.target.value})} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 font-mono font-black text-lg rounded-xl outline-none focus:border-emerald-500 focus:bg-white transition-colors" required />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Date</label>
                    <input type="date" value={payrollForm.date} onChange={e => setPayrollForm({...payrollForm, date: e.target.value})} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 font-mono rounded-xl outline-none focus:border-emerald-500 focus:bg-white cursor-pointer transition-colors" required />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Mode</label>
                    <select value={payrollForm.mode} onChange={e => setPayrollForm({...payrollForm, mode: e.target.value})} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-emerald-500 cursor-pointer transition-colors">
                      <option>UPI / Digital</option>
                      <option>Cash Liquidity</option>
                      <option>Bank Transfer</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Payment Type</label>
                  <select value={payrollForm.type} onChange={e => setPayrollForm({...payrollForm, type: e.target.value})} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-emerald-500 cursor-pointer transition-colors">
                    <option>Standard Salary</option>
                    <option>Bonus / Incentive</option>
                    <option>Advance Salary</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Notes / Deductions</label>
                  <input type="text" placeholder="Add remarks..." value={payrollForm.notes} onChange={e => setPayrollForm({...payrollForm, notes: e.target.value})} className="w-full p-3.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl outline-none focus:border-emerald-500 focus:bg-white transition-colors" />
                </div>

                <div className="pt-4">
                  <button type="submit" disabled={isProcessing} className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider rounded-xl transition-all shadow-md shadow-emerald-200 flex items-center justify-center gap-2">
                    {isProcessing ? <RefreshCw size={18} className="animate-spin" /> : <DollarSign size={18} />} Disburse Network Funds
                  </button>
                  <p className="text-center text-[10px] text-slate-400 mt-3 font-bold">This syncs instantly to {activePayrollEmp.storeId}'s expense ledger.</p>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* AUDIT DASHBOARD (SLIDE OVER) */}
      <AnimatePresence>
        {viewingEmployee && (
          <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm">
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 100, damping: 20 }} className="w-full md:w-[600px] h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col">
              
              <div className="p-6 md:p-8 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <div>
                  <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3">
                    {viewingEmployee.name?.toUpperCase()}
                    {(viewingEmployee.status || "Active") === "Past Employee" && (
                      <span className="text-[9px] bg-rose-100 text-rose-600 px-2.5 py-1 rounded-md uppercase tracking-widest border border-rose-200">Inactive</span>
                    )}
                  </h2>
                  <div className="flex gap-4 mt-2">
                    <p className="text-slate-500 font-mono text-xs font-bold flex items-center gap-1.5"><Phone size={12}/> {viewingEmployee.phone}</p>
                    <p className="text-blue-600 font-mono text-xs font-bold flex items-center gap-1.5"><Building2 size={12}/> {viewingEmployee.storeId}</p>
                  </div>
                </div>
                <button onClick={() => setViewingEmployee(null)} className="p-3 bg-white border border-slate-200 hover:bg-rose-50 hover:text-rose-500 rounded-2xl transition-colors shadow-sm">
                  <X size={20} />
                </button>
              </div>

              <div className="flex bg-slate-50 px-6 md:px-8 pt-2 gap-2 border-b border-slate-200">
                <button onClick={() => setAuditTab("profile")} className={`px-6 py-3.5 text-center font-bold text-xs uppercase tracking-wider rounded-t-xl transition-colors border-t border-l border-r border-transparent ${auditTab === "profile" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:text-slate-800"}`}>
                  <UserIcon size={14} className="inline mr-1.5 mb-0.5" /> Core Bio
                </button>
                <button onClick={() => setAuditTab("payroll")} className={`px-6 py-3.5 text-center font-bold text-xs uppercase tracking-wider rounded-t-xl transition-colors border-t border-l border-r border-transparent ${auditTab === "payroll" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:text-slate-800"}`}>
                  <History size={14} className="inline mr-1.5 mb-0.5" /> Financial Log
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-white">
                {auditTab === "profile" && (
                  <div className="space-y-6">
                    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                      <h3 className="text-xs text-blue-600 font-black uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2"><UserIcon size={16}/> Identity Metrics</h3>
                      <div className="grid grid-cols-2 gap-y-6 gap-x-6">
                        <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Operating Branch</span><span className="font-bold text-slate-800 text-sm bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">{viewingEmployee.storeId || "-"}</span></div>
                        <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Assigned Role</span><span className="font-bold text-slate-800 text-sm">{viewingEmployee.role || "-"}</span></div>
                        <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">System Join Date</span><span className="font-mono font-bold text-slate-800 text-sm">{viewingEmployee.dateOfJoin || "-"}</span></div>
                        <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Base HQ Salary</span><span className="font-mono font-black text-emerald-600 text-xl">₹{viewingEmployee.salary?.toLocaleString() || "0"}</span></div>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                      <h3 className="text-xs text-emerald-600 font-black uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2"><Home size={16}/> Location Profile</h3>
                      <div className="font-bold text-slate-800 text-sm block bg-slate-50 p-5 rounded-2xl border border-slate-200">
                        {viewingEmployee.address || "No address provided in central registry."}
                      </div>
                    </div>
                  </div>
                )}

                {auditTab === "payroll" && (
                  <div className="space-y-4">
                    {!(viewingEmployee.salaryHistory?.length > 0) ? (
                      <div className="text-center text-slate-400 font-medium p-12 bg-slate-50 border border-slate-200 rounded-3xl flex flex-col items-center gap-3">
                        <Banknote size={40} className="text-slate-300" />
                        No HQ payroll transactions logged for this employee.
                      </div>
                    ) : (
                      [...viewingEmployee.salaryHistory].reverse().map((pay, idx) => (
                        <div key={idx} className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start border-b border-slate-100 pb-4 mb-4">
                            <div>
                              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Cleared On</span>
                              <span className="text-sm font-black text-slate-800 font-mono">{pay.date}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Amount Disbursed</span>
                              <span className="text-2xl font-black text-emerald-600 font-mono">₹{pay.amount?.toLocaleString()}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-5 text-xs">
                            <div><span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1.5">Transaction Type</span><span className="font-bold text-slate-700 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">{pay.type}</span></div>
                            <div><span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1.5">Route</span><span className="font-bold text-slate-700">{pay.mode}</span></div>
                            {pay.notes && <div className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1.5">HQ Remarks</span><span className="text-slate-600 font-medium bg-blue-50/50 p-3 rounded-xl border border-blue-100 block">{pay.notes}</span></div>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}